import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Star,
  MapPin,
  Wifi,
  Droplets,
  UtensilsCrossed,
  Car,
  Coffee,
  Dumbbell,
  Home,
  Bookmark,
  Users,
  User,
  ExternalLink,
  Loader2,
  Calendar
} from 'lucide-react';
import { usePlanStore } from '../store/planStore';
import api from '../services/api';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const AMENITY_ICONS = { 
  wifi: Wifi, 
  pool: Droplets, 
  restaurant: UtensilsCrossed, 
  parking: Car,
  spa: Dumbbell,
  breakfast: Coffee,
  beach: Droplets,
  bar: Coffee,
  common_area: Users,
  yoga: Dumbbell,
  heritage: Star,
};

export default function Hotels() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentPlan } = usePlanStore();
  
  const destination = searchParams.get('destination') || currentPlan?.destination || 'Goa';
  const nights = parseInt(searchParams.get('nights') || currentPlan?.duration_days || '3');
  
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [currency, setCurrency] = useState('USD');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  
  useEffect(() => {
    // Set default dates
    const today = new Date();
    const checkInDate = new Date(today);
    checkInDate.setDate(today.getDate() + 7);
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkInDate.getDate() + nights);
    
    setCheckIn(checkInDate.toISOString().split('T')[0]);
    setCheckOut(checkOutDate.toISOString().split('T')[0]);
    
    // Detect currency
    detectCurrency();
  }, [nights]);
  
  useEffect(() => {
    if (checkIn && checkOut) {
      loadHotels();
    }
  }, [destination, checkIn, checkOut, currency]);
  
  const detectCurrency = async () => {
    try {
      const geo = await api.detectLocation();
      if (geo.success && geo.currency) {
        setCurrency(geo.currency);
      }
    } catch (error) {
      console.error('Currency detection error:', error);
    }
  };
  
  const loadHotels = async () => {
    try {
      setLoading(true);
      const result = await api.searchHotels(destination, checkIn, checkOut, {
        guests: 2,
        rooms: 1,
        currency
      });
      
      if (result.success && result.hotels) {
        setHotels(result.hotels);
      }
    } catch (error) {
      console.error('Hotels load error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredHotels = hotels
    .filter(hotel => filter === 'all' || hotel.type === filter)
    .sort((a, b) => {
      if (sortBy === 'price_low') return a.price_per_night - b.price_per_night;
      if (sortBy === 'price_high') return b.price_per_night - a.price_per_night;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });
  
  const formatCurrency = (amount) => {
    const symbols = { USD: '$', INR: '₹', EUR: '€', GBP: '£', JPY: '¥' };
    return `${symbols[currency] || currency}${amount.toLocaleString()}`;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="text-center">
            <h1 className="font-bold text-gray-900">Hotels in {destination}</h1>
            <p className="text-xs text-gray-500">{nights} nights</p>
          </div>
          
          <div className="w-8" />
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Date Selection */}
        <div className="p-4 bg-white/80 backdrop-blur border border-gray-100 shadow" style={{ borderRadius: '1rem' }}>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="text-xs text-gray-500 mb-1 block">Check-in</label>
              <div className="relative">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 text-gray-900 text-sm focus:border-teal-500 outline-none"
                  style={{ borderRadius: '9999px' }}
                />
              </div>
            </div>
            <div className="flex-1 w-full">
              <label className="text-xs text-gray-500 mb-1 block">Check-out</label>
              <div className="relative">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 text-gray-900 text-sm focus:border-teal-500 outline-none"
                  style={{ borderRadius: '9999px' }}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {['all', 'budget', 'comfort', 'luxury'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-5 py-2 font-semibold capitalize whitespace-nowrap transition-all ${
                filter === f
                  ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30'
                  : 'bg-white border-2 border-gray-100 text-gray-600 hover:border-teal-200'
              }`}
              style={{ borderRadius: '9999px' }}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All Hotels' : f}
            </button>
          ))}
        </div>
        
        {/* Sort Options */}
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white border-2 border-gray-100 text-gray-700 font-medium focus:border-teal-400 outline-none"
            style={{ borderRadius: '9999px' }}
            data-testid="sort-select"
          >
            <option value="recommended">Recommended</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
          </select>
        </div>
        
        {/* Hotels List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 size={32} className="animate-spin text-teal-500 mb-4" />
            <p className="text-gray-500">Finding best hotels in {destination}...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredHotels.map((hotel, index) => (
              <motion.div
                key={hotel.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/80 backdrop-blur border border-gray-100 shadow hover:shadow-lg hover:border-teal-200 overflow-hidden transition-all"
                style={{ borderRadius: '1.25rem' }}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="md:w-64 h-48 md:h-auto relative">
                    <img 
                      src={hotel.image} 
                      alt={hotel.name}
                      className="w-full h-full object-cover"
                    />
                    <span className={`absolute top-3 left-3 px-3 py-1 text-xs font-bold capitalize ${
                      hotel.type === 'luxury' ? 'bg-amber-100 text-amber-700' :
                      hotel.type === 'comfort' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-700'
                    }`} style={{ borderRadius: '9999px' }}>
                      {hotel.type}
                    </span>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{hotel.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <MapPin size={14} />
                          <span>{hotel.location}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700" style={{ borderRadius: '0.5rem' }}>
                        <Star size={14} fill="currentColor" />
                        <span className="font-bold">{hotel.rating}</span>
                        <span className="text-xs text-gray-500">({hotel.reviews_count?.toLocaleString()})</span>
                      </div>
                    </div>
                    
                    {/* Amenities */}
                    <div className="flex items-center gap-3 mb-4">
                      {hotel.amenities?.slice(0, 4).map((amenity) => {
                        const Icon = AMENITY_ICONS[amenity] || Wifi;
                        return (
                          <div key={amenity} className="w-8 h-8 bg-gray-100 flex items-center justify-center" style={{ borderRadius: '0.5rem' }} title={amenity}>
                            <Icon size={16} className="text-gray-600" />
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Price & CTA */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div>
                        <span className="text-2xl font-black text-teal-600">{formatCurrency(hotel.price_per_night)}</span>
                        <span className="text-gray-500 text-sm"> / night</span>
                        <p className="text-xs text-gray-400">{formatCurrency(hotel.total_price)} total for {hotel.nights || nights} nights</p>
                      </div>
                      <a 
                        href={hotel.booking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all"
                        style={{ borderRadius: '9999px' }}
                        data-testid={`book-${hotel.id}`}
                      >
                        Book Now
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Info Note */}
        <div className="p-4 bg-teal-50 border border-teal-200 text-center" style={{ borderRadius: '1rem' }}>
          <p className="text-teal-700 text-sm">
            {"🏨 You'll be redirected to Booking.com to complete your reservation. Prices may vary."}
          </p>
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      <FloatingSupport />
    </div>
  );
}
